import React, { useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { renderModal } from 'utils/modal_util';

import TooltipModalContent from 'components/shared/TooltipModalContent';
import Modal from 'components/shared/Modal';
import Button from 'components/shared/Button';
import Filter from 'components/shared/Filter';

import CustomSizePicker from 'components/features/ProductSidebar/CustomSizePickerContainer';
import ProductButton from 'components/features/ProductSidebar/ProductButtonContainer';

import activeEditor from 'domains/active_editor';
import canvases from 'domains/canvases';
import productsModule from 'domains/products';
import ui from 'domains/ui';

import type { TooltipModalContentProps } from 'components/shared/TooltipModalContent/TooltipModalContent';

function Products() {
  const [tooltipDialog, setTooltipDialog] = useState<React.ReactNode>(null);

  const dispatch = useAppDispatch();
  const products = useAppSelector(activeEditor.selectors.filteredProducts);
  const activeSubcatalog = useAppSelector(activeEditor.selectors.subcatalog);
  const isCustomProduct = useAppSelector(activeEditor.selectors.isCustomProduct);
  const productFilters = useAppSelector(activeEditor.selectors.subcatalogFilters);
  const aspectRatios = useAppSelector(activeEditor.selectors.aspectRatioFilters);
  const productFilter = useAppSelector(productsModule.selectors.appliedFilter);
  const showSidebarProducts = useAppSelector(ui.selectors.showSidebarProducts);
  const hasAdvancedFilters = useAppSelector(activeEditor.selectors.hasAdvancedFilters);
  const selectedAspectRatio = useAppSelector(productsModule.selectors.aspectRatioFilter);

  if (!activeSubcatalog) return null;

  const { label_ui: productLabel } = activeSubcatalog;

  const handleTooltipSelect = (productId: number) => {
    dispatch(canvases.actions.changeActiveCanvasConfig.product(productId));
    setTooltipDialog(null);
  };

  const tooltipModalData: TooltipModalContentProps[] = useMemo(() => {
    return products.map((product) => {
      const {
        id,
        read_only: readOnly,
        tooltip_title: tooltipTitle,
        tooltip_description: tooltipDescription,
        tooltip_img: tooltipImg,
        tooltip_url: tooltipUrl,
      } = product;

      return {
        id,
        readOnly,
        tooltipTitle,
        tooltipDescription,
        tooltipImg,
        tooltipUrl,
        parentId: null,
        handleTooltipSelect,
      };
    });
  }, [products]);

  function handleOpenTooltipModal(productId: number, clickedProductButtonId: string) {
    const pageStartIndex = tooltipModalData.findIndex((product) => product.id === productId);

    setTooltipDialog(
      <Modal
        labelledBy="product-tooltip-modal"
        lastFocusedElementId={clickedProductButtonId}
        onClose={() => setTooltipDialog(null)}
        title={activeSubcatalog?.label_ui || ''}
        pageStartIndex={pageStartIndex}
      >
        {tooltipModalData.map((product) => (
          <TooltipModalContent key={product.id} {...product} />
        ))}
      </Modal>
    );
  }

  function handleSelectFilter(filter: string) {
    dispatch(productsModule.actions.setProductFilter(filter));
  }

  function handleSelectAspectRatio(ratio: string) {
    dispatch(productsModule.actions.setAspectRatioFilter(ratio));
  }

  function handleShowAdvancedFilters() {
    dispatch(ui.actions.setShowAdvancedFilters(true));
  }

  return (
    <React.Fragment>
      <div className={`sidebar-section${!showSidebarProducts ? ' hide-products' : ''}`}>
        <header className="sidebar-section__header">
          {productLabel ? <h2>{productLabel}</h2> : <h2>Size</h2>}
          {hasAdvancedFilters && (
            <div className="advanced-filter-button">
              <Button type="bare" onClick={handleShowAdvancedFilters}>
                Filter Designs <FontAwesomeIcon icon="sliders-h" size="lg" />
              </Button>
            </div>
          )}
        </header>
        <Filter
          filterUI={activeSubcatalog.filter_ui}
          selectedFilter={isCustomProduct ? 'Custom' : productFilter || 'All'}
          filters={productFilters}
          selectedAspectRatio={selectedAspectRatio}
          aspectRatios={aspectRatios}
          filterType="products"
          handleSelectFilter={handleSelectFilter}
          onSelectAspectRatio={handleSelectAspectRatio}
        />
        {isCustomProduct && <CustomSizePicker />}
        {!isCustomProduct && (
          <ul className="products">
            {products.map((product) => (
              <li className="product" key={product.id}>
                <ProductButton id={product.id} handleOpenTooltipModal={handleOpenTooltipModal} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {tooltipDialog && renderModal(tooltipDialog)}
    </React.Fragment>
  );
}

export default Products;
